import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Phone, Send, Calendar, Calculator, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "../lib/config";
import Logo from "./Logo";

// Live chat widget — opens a smooth popup with quick replies that deep-link
// to native SMS / phone, plus links to Book and Estimate flows.
export default function LiveChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: "agent",
      text: "Hi, I'm JDSN's assistant. How can I help you today? Tap a quick reply below or text us — we usually reply in minutes.",
    },
  ]);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages]);

  const sendDraft = () => {
    const t = draft.trim();
    if (!t) return;
    // Open native SMS with prefilled text
    window.location.href = `sms:${BRAND.phoneTel}?&body=${encodeURIComponent(t)}`;
    setMessages((m) => [
      ...m,
      { from: "me", text: t },
      { from: "agent", text: "Opening your messages app to send this to JDSN…" },
    ]);
    setDraft("");
  };

  const quickReplies = [
    { label: "💰 Get a quote", action: "estimate" },
    { label: "📅 Book a service", action: "book" },
    { label: "📞 Call us", action: "call" },
    { label: "💬 Text us", action: "text" },
    { label: "🕒 Hours", action: "hours" },
  ];

  const handleQuick = (action) => {
    if (action === "call") { window.location.href = `tel:${BRAND.phoneTel}`; return; }
    if (action === "text") { window.location.href = `sms:${BRAND.phoneTel}`; return; }
    if (action === "hours") {
      setMessages((m) => [...m, { from: "me", text: "What are your hours?" }, { from: "agent", text: "We're open Monday–Saturday, 7am–7pm. Sundays by appointment. Texts answered 7 days a week 🌿" }]);
      return;
    }
    if (action === "estimate" || action === "book") {
      // Close chat and navigate via anchor
      setOpen(false);
      window.location.hash = "";
      setTimeout(() => {
        window.location.href = action === "estimate" ? "/estimate" : "/book";
      }, 150);
    }
  };

  return (
    <>
      {/* Floating launcher button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="livechat-launcher"
        aria-label="Open live chat"
        className={`fixed right-4 z-30 w-14 h-14 rounded-full bg-gradient-to-br from-[#10B981] to-[#047857] text-white shadow-2xl shadow-emerald-600/30 flex items-center justify-center active:scale-95 hover:scale-105 transition-all md:bottom-6 ${open ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)" }}
      >
        <MessageSquare className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-300 border-2 border-white animate-pulse" />
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed right-3 left-3 sm:left-auto sm:right-5 z-50 sm:w-[380px] max-h-[78vh] flex flex-col bg-white rounded-3xl border border-gray-200 shadow-2xl shadow-black/15 overflow-hidden animate-in"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)" }}
          data-testid="livechat-panel"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-br from-[#0A2E1E] via-[#093924] to-[#062818] text-white px-5 pt-5 pb-4">
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-emerald-400/15 blur-2xl pointer-events-none" />
            <div className="relative flex items-center gap-3">
              <Logo size={42} withRing />
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-extrabold tracking-tight font-[Outfit] leading-tight">JDSN Lawn Care</div>
                <div className="mt-0.5 text-[12px] text-emerald-100/90 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
                  Online — replies in minutes
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="livechat-close"
                className="w-9 h-9 rounded-full hover:bg-white/10 active:bg-white/15 flex items-center justify-center"
              >
                <X className="w-5 h-5 text-white/90" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5 bg-[#F7FAF8]" data-testid="livechat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[82%] px-3.5 py-2.5 text-[13.5px] leading-snug rounded-2xl ${m.from === "me" ? "bg-[#047857] text-white rounded-br-md" : "bg-white text-gray-800 border border-gray-200 rounded-bl-md"}`}>
                  {m.text}
                </div>
              </div>
            ))}
          </div>

          {/* Quick replies */}
          <div className="px-3 py-2.5 border-t border-gray-100 bg-white flex gap-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            {quickReplies.map((q) => (
              <button
                key={q.action}
                type="button"
                onClick={() => handleQuick(q.action)}
                data-testid={`livechat-quick-${q.action}`}
                className="shrink-0 h-9 px-3.5 rounded-full bg-emerald-50 text-[#047857] text-[12.5px] font-bold border border-emerald-100 hover:bg-emerald-100 transition-colors"
              >
                {q.label}
              </button>
            ))}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); sendDraft(); }}
            className="flex items-center gap-2 px-3 py-3 border-t border-gray-100 bg-white"
            data-testid="livechat-composer"
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Type a message…"
              data-testid="livechat-input"
              className="flex-1 h-11 px-4 rounded-full bg-gray-100 border border-transparent focus:border-emerald-300 focus:bg-white focus:outline-none text-[14px] font-medium text-gray-900 placeholder:text-gray-400"
            />
            <button
              type="submit"
              data-testid="livechat-send"
              aria-label="Send"
              className="shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-[#10B981] to-[#047857] text-white flex items-center justify-center active:scale-95 transition-transform"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>

          {/* Footnote */}
          <div className="px-4 pb-3 pt-1 bg-white text-[10.5px] text-gray-400 text-center">
            Messages open your phone's SMS app to {BRAND.phone}
          </div>
        </div>
      )}
    </>
  );
}
